import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Send, Check, Hammer } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Kicker } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { API } from "@/constants/assets";

const TYPES = [
  { key: "t_window", value: "Fenêtre" },
  { key: "t_door_ext", value: "Porte d'entrée" },
  { key: "t_door_int", value: "Porte intérieure" },
  { key: "t_sliding", value: "Porte coulissante" },
  { key: "t_shutter", value: "Volet / Persienne" },
];
const WOODS = [
  { key: "w_chene", value: "Chêne de France" },
  { key: "w_sapin", value: "Sapin du Nord" },
  { key: "w_meranti", value: "Meranti" },
  { key: "w_tilleul", value: "Tilleul" },
  { key: "w_noyer", value: "Noyer" },
];
const GLAZINGS = [
  { key: "g_double", value: "Double vitrage 4/16/4" },
  { key: "g_triple", value: "Triple vitrage Low-E Argon" },
  { key: "g_ancien", value: "Vitrage à l'ancienne (petits-bois)" },
];
const SHAPES = [
  { key: "sh_rect", value: "Rectangulaire" },
  { key: "sh_cintre", value: "Cintrée" },
  { key: "sh_oeil", value: "Œil-de-bœuf" },
  { key: "sh_autre", value: "Forme spéciale" },
];

const OptionCard = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`text-left px-4 py-3.5 border transition-all duration-300 text-sm ${
      active
        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F7F4EE]"
        : "hairline border text-[#B8B0A2] hover:border-[rgba(212,175,55,0.6)] hover:text-[#F7F4EE]"
    }`}
  >
    <span className="flex items-center justify-between gap-2">
      {children}
      {active && <Check size={14} className="text-[#D4AF37] shrink-0" />}
    </span>
  </button>
);

const inputCls = "field w-full px-4 py-3 text-sm";

export function Configurator() {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const [form, setForm] = useState({
    product_type: "", quantity: 1, wood: "", glazing: "", profile: "68 mm",
    height: "", width: "", shape: "",
    sculpture: false, copie_ancien: false, details: "",
    name: "", email: "", phone: "", project_address: "", message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = [t("cfg_s1"), t("cfg_s2"), t("cfg_s3"), t("cfg_s4"), t("cfg_s5")];

  const canNext = () => {
    if (step === 0) return !!form.product_type;
    if (step === 4) return form.name.trim().length > 1 && /.+@.+\..+/.test(form.email);
    return true;
  };

  const submit = async () => {
    if (!canNext()) { toast.error(t("cfg_name") + " / " + t("cfg_email")); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/requests`, form);
      setReference(data.reference);
      toast.success(t("cfg_ok_t"));
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally {
      setSending(false);
    }
  };

  const summaryRows = [
    [t("cfg_type"), form.product_type],
    [t("cfg_qty"), form.product_type ? String(form.quantity) : ""],
    [t("cfg_wood"), form.wood],
    [t("cfg_glazing"), form.glazing],
    [t("cfg_profile"), form.wood ? form.profile : ""],
    [t("cfg_shape"), form.shape],
    ["H × L", form.height || form.width ? `${form.height || "—"} × ${form.width || "—"} mm` : ""],
  ].filter(([, v]) => v);

  return (
    <section id="expression" data-testid="configurator-section" className="relative py-28 md:py-40 bg-[#0B0A08]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal><Kicker>{t("cfg_kicker")}</Kicker></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif-d text-4xl md:text-6xl mt-6 text-[#F7F4EE]">{t("cfg_title")}</h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-4 text-sm md:text-base text-[#B8B0A2] max-w-xl">{t("cfg_sub")}</p>
        </Reveal>

        {reference ? (
          <motion.div
            data-testid="configurator-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-16 border border-[#D4AF37]/50 bg-[#14120F] p-10 md:p-16 text-center max-w-2xl mx-auto"
          >
            <span className="mx-auto w-16 h-16 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <Check size={26} />
            </span>
            <h3 className="font-serif-d text-3xl md:text-4xl mt-6 text-[#F7F4EE]">{t("cfg_ok_t")}</h3>
            <p className="mt-4 text-sm text-[#B8B0A2]">{t("cfg_ok_d")}</p>
            <p data-testid="configurator-reference" className="mt-3 font-mono-d text-xl gold-text tracking-widest">{reference}</p>
            <button
              data-testid="configurator-restart-button"
              onClick={() => { setReference(null); setStep(0); setForm({ product_type: "", quantity: 1, wood: "", glazing: "", profile: "68 mm", height: "", width: "", shape: "", sculpture: false, copie_ancien: false, details: "", name: "", email: "", phone: "", project_address: "", message: "" }); }}
              className="mt-8 gold-btn border border-[#D4AF37] px-6 py-3 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]"
            >
              {t("cfg_restart")}
            </button>
          </motion.div>
        ) : (
          <div className="mt-14 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap gap-2 mb-10" data-testid="configurator-steps">
                {steps.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`configurator-step-tab-${i}`}
                    onClick={() => i < step && setStep(i)}
                    className={`flex items-center gap-2 px-3.5 py-2 border text-[10px] font-mono-d tracking-[0.15em] uppercase transition-colors duration-300 ${
                      i === step ? "border-[#D4AF37] text-[#D4AF37]" : i < step ? "hairline border text-[#B8B0A2] cursor-pointer hover:text-[#F7F4EE]" : "border-[rgba(197,160,89,0.12)] text-[#7A7367] cursor-default"
                    }`}
                  >
                    <span className="opacity-60">0{i + 1}</span> {s}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  {step === 0 && (
                    <div data-testid="configurator-step-product-type">
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mb-4">{t("cfg_type")}</label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {TYPES.map((ty) => (
                          <OptionCard key={ty.key} testid={`configurator-type-${ty.key}`} active={form.product_type === ty.value} onClick={() => set("product_type", ty.value)}>
                            {t(ty.key)}
                          </OptionCard>
                        ))}
                      </div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mt-8 mb-4">{t("cfg_qty")}</label>
                      <input data-testid="configurator-input-quantity" type="number" min="1" max="200" value={form.quantity} onChange={(e) => set("quantity", parseInt(e.target.value) || 1)} className={`${inputCls} w-32`} />
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mb-4">{t("cfg_wood")}</label>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {WOODS.map((w) => (
                          <OptionCard key={w.key} testid={`configurator-wood-${w.key}`} active={form.wood === w.value} onClick={() => set("wood", w.value)}>
                            {t(w.key)}
                          </OptionCard>
                        ))}
                      </div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mt-8 mb-4">{t("cfg_glazing")}</label>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {GLAZINGS.map((g) => (
                          <OptionCard key={g.key} testid={`configurator-glazing-${g.key}`} active={form.glazing === g.value} onClick={() => set("glazing", g.value)}>
                            {t(g.key)}
                          </OptionCard>
                        ))}
                      </div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mt-8 mb-4">{t("cfg_profile")}</label>
                      <div className="flex gap-3">
                        {["68 mm", "92 mm"].map((p) => (
                          <OptionCard key={p} testid={`configurator-profile-${p.replace(" ", "")}`} active={form.profile === p} onClick={() => set("profile", p)}>
                            {p}
                          </OptionCard>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mb-3">{t("cfg_height")}</label>
                          <input data-testid="configurator-input-height" type="number" min="0" placeholder="1400" value={form.height} onChange={(e) => set("height", e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mb-3">{t("cfg_width")}</label>
                          <input data-testid="configurator-input-width" type="number" min="0" placeholder="900" value={form.width} onChange={(e) => set("width", e.target.value)} className={inputCls} />
                        </div>
                      </div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mt-8 mb-4">{t("cfg_shape")}</label>
                      <div className="grid sm:grid-cols-4 gap-3">
                        {SHAPES.map((s) => (
                          <OptionCard key={s.key} testid={`configurator-shape-${s.key}`} active={form.shape === s.value} onClick={() => set("shape", s.value)}>
                            {t(s.key)}
                          </OptionCard>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div>
                      <div className="space-y-3">
                        <OptionCard testid="configurator-sculpture-checkbox" active={form.sculpture} onClick={() => set("sculpture", !form.sculpture)}>
                          <span className="flex items-center gap-3"><Hammer size={15} className="text-[#C5A059]" /> {t("cfg_sculpt")}</span>
                        </OptionCard>
                        <OptionCard testid="configurator-copie-checkbox" active={form.copie_ancien} onClick={() => set("copie_ancien", !form.copie_ancien)}>
                          {t("cfg_copie")}
                        </OptionCard>
                      </div>
                      <label className="block text-xs font-mono-d tracking-[0.25em] uppercase text-[#C5A059] mt-8 mb-3">{t("cfg_details")}</label>
                      <textarea data-testid="configurator-input-details" rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} className={`${inputCls} resize-none`} />
                    </div>
                  )}

                  {step === 4 && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="configurator-input-name" placeholder={t("cfg_name")} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                      <input data-testid="configurator-input-email" type="email" placeholder={t("cfg_email")} value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                      <input data-testid="configurator-input-phone" placeholder={t("cfg_phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
                      <input data-testid="configurator-input-address" placeholder={t("cfg_addr")} value={form.project_address} onChange={(e) => set("project_address", e.target.value)} className={inputCls} />
                      <textarea data-testid="configurator-input-message" rows={3} placeholder={t("cfg_msg")} value={form.message} onChange={(e) => set("message", e.target.value)} className={`${inputCls} sm:col-span-2 resize-none`} />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                <button
                  data-testid="configurator-prev-button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.2em] uppercase text-[#B8B0A2] border border-[rgba(247,244,238,0.2)] disabled:opacity-30 hover:border-[#F7F4EE] hover:text-[#F7F4EE] transition-colors duration-300"
                >
                  <ArrowLeft size={14} /> {t("cfg_prev")}
                </button>
                {step < 4 ? (
                  <button
                    data-testid="configurator-next-button"
                    onClick={() => canNext() ? setStep((s) => s + 1) : toast.error(t("cfg_type"))}
                    className="gold-btn inline-flex items-center gap-2 border border-[#D4AF37] px-7 py-3 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]"
                  >
                    {t("cfg_next")} <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    data-testid="configurator-submit-button"
                    onClick={submit}
                    disabled={sending}
                    className="gold-btn inline-flex items-center gap-2 border border-[#D4AF37] px-7 py-3 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] disabled:opacity-50"
                  >
                    {sending ? "…" : t("cfg_submit")} <Send size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="lg:col-span-4">
              <div data-testid="configurator-summary" className="lg:sticky lg:top-28 border hairline bg-[#14120F] p-7">
                <h4 className="font-mono-d text-[10px] tracking-[0.35em] uppercase text-[#C5A059]">{t("cfg_summary")}</h4>
                {summaryRows.length === 0 ? (
                  <p className="mt-5 text-sm text-[#7A7367] font-serif-d italic">Votre ouvrage prend forme ici…</p>
                ) : (
                  <dl className="mt-5 space-y-3.5">
                    {summaryRows.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 text-sm border-b border-[rgba(197,160,89,0.12)] pb-3">
                        <dt className="text-[#7A7367]">{k}</dt>
                        <dd className="text-[#F7F4EE] text-right">{v}</dd>
                      </div>
                    ))}
                    {(form.sculpture || form.copie_ancien) && (
                      <div className="pt-1 flex flex-wrap gap-2">
                        {form.sculpture && <span className="text-[10px] font-mono-d tracking-widest uppercase border border-[#D4AF37]/50 text-[#D4AF37] px-2.5 py-1">Maître sculpteur</span>}
                        {form.copie_ancien && <span className="text-[10px] font-mono-d tracking-widest uppercase border border-[#D4AF37]/50 text-[#D4AF37] px-2.5 py-1">Copie d'ancien</span>}
                      </div>
                    )}
                  </dl>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
